import { useNavigate } from 'react-router-dom';
import SeoHead from '../../components/SeoHead';

const sections = [
  {
    id: 1,
    icon: 'ri-mind-map',
    color: 'sky',
    title: 'Mentalidad y preparación previa',
    content: [
      {
        subtitle: 'La investigación es tu mayor ventaja',
        text: 'Antes de cualquier entrevista internacional, dedica al menos 2-3 horas a investigar la empresa: su historia, valores, productos o servicios, presencia en el mercado, noticias recientes y cultura corporativa. Los reclutadores europeos valoran enormemente que el candidato conozca la empresa en profundidad.',
      },
      {
        subtitle: 'Conoce el puesto al detalle',
        text: 'Lee la oferta de empleo varias veces y extrae las palabras clave. Prepara ejemplos concretos de tu experiencia que demuestren cada competencia requerida. Usa la técnica STAR (Situación, Tarea, Acción, Resultado) para estructurar tus respuestas.',
      },
      {
        subtitle: 'Prepara tus preguntas',
        text: 'En Europa, que el candidato haga preguntas inteligentes al final de la entrevista es señal de interés y madurez profesional. Prepara al menos 5 preguntas sobre el equipo, los retos del puesto, la cultura de la empresa y las expectativas a 6 meses. Nunca preguntes sobre salario en la primera entrevista.',
      },
    ],
  },
  {
    id: 2,
    icon: 'ri-question-answer-line',
    color: 'emerald',
    title: 'Preguntas frecuentes en entrevistas europeas',
    content: [
      {
        subtitle: '"Tell me about yourself" / "Háblame de ti"',
        text: 'Esta es la pregunta más común y la más importante. Prepara una respuesta de 2-3 minutos que cubra: quién eres profesionalmente, tu trayectoria más relevante, tus logros clave y por qué estás interesado en este puesto específico. No es un resumen de tu CV, es tu propuesta de valor personal.',
      },
      {
        subtitle: '"Why do you want to work here?" / "¿Por qué esta empresa?"',
        text: 'Demuestra que has investigado. Menciona aspectos concretos: un proyecto reciente de la empresa, sus valores, su posición en el mercado o su cultura de innovación. Conecta esos aspectos con tus motivaciones personales y profesionales. La respuesta genérica es el error más común.',
      },
      {
        subtitle: '"What is your greatest weakness?" / "¿Cuál es tu mayor debilidad?"',
        text: 'No digas "soy perfeccionista". Elige una debilidad real pero no crítica para el puesto, y explica qué estás haciendo activamente para mejorarla. Ejemplo: "Tiendo a profundizar demasiado en los detalles, lo que a veces ralentiza mi ritmo. He aprendido a establecer límites de tiempo para cada tarea y a priorizar el avance sobre la perfección."',
      },
    ],
  },
  {
    id: 3,
    icon: 'ri-star-smile-line',
    color: 'amber',
    title: 'Técnica STAR: respuestas con impacto',
    content: [
      {
        subtitle: 'Qué es la técnica STAR',
        text: 'STAR es el método estándar para responder preguntas de comportamiento en entrevistas europeas. S = Situación (contexto), T = Tarea (tu responsabilidad), A = Acción (qué hiciste exactamente), R = Resultado (qué conseguiste, con datos si es posible). Esta estructura demuestra pensamiento lógico y orientación a resultados.',
      },
      {
        subtitle: 'Ejemplo aplicado',
        text: 'Pregunta: "Cuéntame una vez que tuviste que gestionar un conflicto en el equipo." Respuesta STAR: "En mi anterior empresa (S), era responsable de coordinar un equipo de 6 personas con plazos muy ajustados (T). Dos miembros tenían un conflicto de comunicación que afectaba la productividad. Organicé una reunión individual con cada uno y luego una sesión conjunta para alinear expectativas (A). El equipo mejoró su rendimiento un 30% en el siguiente sprint (R)."',
      },
      {
        subtitle: 'Prepara 8-10 historias STAR',
        text: 'Antes de la entrevista, prepara al menos 8-10 historias STAR de tu experiencia que cubran: liderazgo, resolución de conflictos, gestión del tiempo, trabajo bajo presión, iniciativa propia, trabajo en equipo, fracaso y aprendizaje, y logro destacado. Estas historias se pueden adaptar a casi cualquier pregunta.',
      },
    ],
  },
  {
    id: 4,
    icon: 'ri-global-line',
    color: 'teal',
    title: 'Entrevistas en inglés u otro idioma',
    content: [
      {
        subtitle: 'Preparación lingüística específica',
        text: 'Si la entrevista es en inglés u otro idioma europeo, practica en voz alta, no solo mentalmente. Grábate respondiendo preguntas típicas y escúchate. Usa plataformas como iTalki o Preply para practicar con hablantes nativos. El vocabulario técnico de tu sector en el idioma de la entrevista es imprescindible.',
      },
      {
        subtitle: 'Qué hacer si no entiendes una pregunta',
        text: 'Es completamente aceptable pedir que repitan o aclaren una pregunta. Di: "Could you please rephrase that?" o "I want to make sure I understand correctly, are you asking about...?" Esto demuestra profesionalidad, no debilidad. Nunca respondas a una pregunta que no has entendido bien.',
      },
      {
        subtitle: 'Velocidad y claridad',
        text: 'Habla más despacio de lo que crees necesario. Los reclutadores europeos prefieren claridad a velocidad. Haz pausas entre ideas. Si necesitas un momento para pensar, di: "That\'s a great question, let me think for a moment." El silencio reflexivo es valorado positivamente en culturas como la alemana o la suiza.',
      },
    ],
  },
  {
    id: 5,
    icon: 'ri-video-chat-line',
    color: 'violet',
    title: 'Entrevistas por videollamada',
    content: [
      {
        subtitle: 'Configuración técnica y visual',
        text: 'Prueba la conexión, cámara y micrófono al menos 30 minutos antes. Usa auriculares para evitar el eco. Fondo neutro y ordenado, o usa un fondo virtual profesional. Iluminación frontal (no de espaldas a una ventana). La cámara a la altura de los ojos, no desde abajo. Viste de forma profesional de cintura para arriba.',
      },
      {
        subtitle: 'Lenguaje corporal en pantalla',
        text: 'Mira a la cámara, no a la pantalla, cuando hablas. Esto simula el contacto visual. Asiente con la cabeza para mostrar que escuchas. Sonríe de forma natural. Evita moverte demasiado o gesticular fuera del encuadre. Mantén una postura erguida y relajada durante toda la entrevista.',
      },
      {
        subtitle: 'Gestión de imprevistos técnicos',
        text: 'Si hay problemas técnicos, mantén la calma y comunícalo con naturalidad: "I\'m having a brief technical issue, please bear with me." Ten el número de teléfono del entrevistador por si necesitas cambiar a una llamada. Los problemas técnicos son comprensibles; cómo los gestionas dice mucho de ti.',
      },
    ],
  },
  {
    id: 6,
    icon: 'ri-money-euro-circle-line',
    color: 'rose',
    title: 'Negociación salarial en Europa',
    content: [
      {
        subtitle: 'Cuándo y cómo hablar de salario',
        text: 'En Europa, el salario se negocia generalmente en la segunda o tercera entrevista, o cuando la empresa lo introduce. Investiga el rango salarial del puesto en el país de destino usando plataformas como Glassdoor, LinkedIn Salary o Gehalt.de (Alemania). Siempre da un rango, no una cifra exacta, y justifícalo con tu experiencia.',
      },
      {
        subtitle: 'Diferencias por país',
        text: 'En Alemania y Suiza es habitual que la empresa pregunte las expectativas salariales en el formulario de candidatura. En Países Bajos se negocia con más apertura. En Austria la negociación es más formal. Investiga si el salario incluye beneficios como seguro médico, coche de empresa, bonus o días de vacaciones adicionales.',
      },
      {
        subtitle: 'Cómo responder a la pregunta salarial',
        text: 'Ejemplo: "Based on my research and my 8 years of experience in this field, I\'m looking for a range between X and Y. However, I\'m open to discussing the full compensation package." Nunca des una cifra inferior a tu expectativa real, ya que es difícil negociar al alza después.',
      },
    ],
  },
  {
    id: 7,
    icon: 'ri-mail-check-line',
    color: 'orange',
    title: 'Seguimiento post-entrevista',
    content: [
      {
        subtitle: 'El email de agradecimiento',
        text: 'Envía un email de agradecimiento dentro de las 24 horas siguientes a la entrevista. Menciona algo específico de la conversación para demostrar que estuviste atento. Reitera tu interés en el puesto y añade brevemente algo que no pudiste mencionar o que quieras reforzar. Este gesto es muy valorado en culturas como la alemana y la anglosajona.',
      },
      {
        subtitle: 'Cuándo hacer seguimiento',
        text: 'Si no recibes respuesta en el plazo indicado, es completamente apropiado enviar un email de seguimiento educado. Espera al menos 5 días hábiles después del plazo prometido. Di: "I wanted to follow up on my application for [puesto]. I remain very interested and would be happy to provide any additional information." No llames por teléfono salvo que te lo hayan indicado.',
      },
      {
        subtitle: 'Gestión del rechazo',
        text: 'Si recibes un rechazo, responde agradeciendo el tiempo y preguntando si pueden darte feedback sobre tu candidatura. En Europa, especialmente en Alemania y Países Bajos, es habitual que den feedback constructivo. Este feedback es valiosísimo para mejorar en futuras entrevistas. Mantén siempre una actitud profesional.',
      },
    ],
  },
];

const tips = [
  { icon: 'ri-check-double-line', text: 'Investiga la empresa a fondo antes de la entrevista' },
  { icon: 'ri-check-double-line', text: 'Prepara 8-10 historias STAR de tu experiencia' },
  { icon: 'ri-check-double-line', text: 'Practica en voz alta, no solo mentalmente' },
  { icon: 'ri-check-double-line', text: 'Llega (o conéctate) 10 minutos antes' },
  { icon: 'ri-check-double-line', text: 'Lleva copias de tu CV y referencias si es presencial' },
  { icon: 'ri-check-double-line', text: 'Envía un email de agradecimiento en las 24h siguientes' },
];

const colorMap: Record<string, { bg: string; text: string; border: string; light: string }> = {
  sky: { bg: 'bg-sky-600', text: 'text-sky-600', border: 'border-sky-200', light: 'bg-sky-50' },
  emerald: { bg: 'bg-emerald-600', text: 'text-emerald-600', border: 'border-emerald-200', light: 'bg-emerald-50' },
  amber: { bg: 'bg-amber-600', text: 'text-amber-600', border: 'border-amber-200', light: 'bg-amber-50' },
  teal: { bg: 'bg-teal-600', text: 'text-teal-600', border: 'border-teal-200', light: 'bg-teal-50' },
  violet: { bg: 'bg-violet-600', text: 'text-violet-600', border: 'border-violet-200', light: 'bg-violet-50' },
  rose: { bg: 'bg-rose-600', text: 'text-rose-600', border: 'border-rose-200', light: 'bg-rose-50' },
  orange: { bg: 'bg-orange-600', text: 'text-orange-600', border: 'border-orange-200', light: 'bg-orange-50' },
};

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const guiaEntrevistasSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Cómo preparar entrevistas internacionales',
  description: 'Estrategias y técnicas para afrontar entrevistas con empresas europeas: técnica STAR, preguntas frecuentes, negociación salarial y seguimiento post-entrevista.',
  url: `${siteUrl}/guia-entrevistas-internacionales`,
  author: { '@type': 'Organization', name: 'BN ĀGENZIA' },
  publisher: {
    '@type': 'Organization',
    name: 'BN ĀGENZIA',
    url: siteUrl,
  },
  inLanguage: 'es',
  about: { '@type': 'Thing', name: 'Entrevistas de trabajo internacionales' },
  dateModified: '2025-07-14',
};

export default function GuiaEntrevistasPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 print:bg-white">
      <SeoHead
        title="Cómo preparar entrevistas internacionales – Guía profesional | BN ĀGENZIA"
        description="Técnica STAR, preguntas frecuentes en entrevistas europeas, negociación salarial y seguimiento post-entrevista. Guía elaborada por expertos en selección internacional."
        keywords="entrevistas internacionales, preparar entrevista Europa, técnica STAR, negociación salarial Europa, entrevista trabajo Alemania"
        canonical="/guia-entrevistas-internacionales"
        schema={guiaEntrevistasSchema}
      />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm print:hidden">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/perfil?tab=recursos')}
              className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#1A2B4A] transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              Volver a recursos
            </button>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold">
                Recurso gratuito
              </span>
              <button
                onClick={() => window.print()}
                className="flex items-center gap-2 px-4 py-2 bg-[#1A2B4A] text-white text-sm font-medium rounded-lg hover:bg-[#2A3B5A] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-printer-line"></i>
                Imprimir / Guardar PDF
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10 print:py-4 print:px-8">
        <div className="bg-gradient-to-r from-[#1A2B4A] to-[#2A3B5A] rounded-2xl p-10 mb-10 print:rounded-none print:mb-6">
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-user-voice-line text-white text-3xl"></i>
            </div>
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="px-3 py-1 bg-sky-400/20 text-sky-300 border border-sky-400/30 rounded-full text-xs font-semibold uppercase tracking-wide">
                  Guía profesional
                </span>
                <span className="text-white/50 text-xs">7 secciones · Lectura: ~15 min</span>
              </div>
              <h1 className="text-3xl font-bold text-white mb-3 leading-tight">
                Cómo preparar entrevistas internacionales
              </h1>
              <p className="text-white/80 text-base leading-relaxed max-w-2xl">
                Estrategias y técnicas para afrontar entrevistas con empresas europeas, incluyendo preguntas frecuentes, técnica STAR, negociación salarial y seguimiento post-entrevista.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-10">
          <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-list-check-2 text-[#1A2B4A]"></i>
            Contenido de la guía
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sections.map((section) => {
              const c = colorMap[section.color];
              return (
                <a
                  key={section.id}
                  href={`#seccion-${section.id}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <span className={`w-7 h-7 ${c.bg} text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0`}>
                    {section.id}
                  </span>
                  <span className="text-sm text-gray-700 group-hover:text-[#1A2B4A] font-medium transition-colors">
                    {section.title}
                  </span>
                </a>
              );
            })}
          </div>
        </div>

        <div className="space-y-8">
          {sections.map((section) => {
            const c = colorMap[section.color];
            return (
              <div
                key={section.id}
                id={`seccion-${section.id}`}
                className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden print:break-inside-avoid"
              >
                <div className={`${c.light} ${c.border} border-b px-6 py-5 flex items-center gap-4`}>
                  <div className={`w-11 h-11 ${c.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <i className={`${section.icon} text-white text-xl`}></i>
                  </div>
                  <div>
                    <span className={`text-xs font-semibold ${c.text} uppercase tracking-wide`}>
                      Sección {section.id}
                    </span>
                    <h2 className="text-lg font-bold text-gray-900 leading-tight">{section.title}</h2>
                  </div>
                </div>
                <div className="p-6 space-y-6">
                  {section.content.map((item, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className={`w-1 rounded-full ${c.bg} flex-shrink-0 mt-1`} style={{ minHeight: '100%' }}></div>
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-1.5">{item.subtitle}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 bg-white rounded-xl border border-gray-200 shadow-sm p-8 print:break-inside-avoid">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 bg-[#1A2B4A] rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-checkbox-multiple-line text-white text-xl"></i>
            </div>
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Antes de la entrevista</span>
              <h2 className="text-lg font-bold text-gray-900">Checklist final</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tips.map((tip, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                <i className="ri-check-double-line text-emerald-600 text-lg flex-shrink-0 mt-0.5"></i>
                <p className="text-sm text-gray-700 leading-relaxed">{tip.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 bg-gradient-to-r from-[#1A2B4A] to-[#2A3B5A] rounded-xl p-8 text-center print:hidden">
          <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-customer-service-2-line text-white text-2xl"></i>
          </div>
          <h3 className="text-xl font-bold text-white mb-2">¿Quieres preparar tu entrevista con nosotros?</h3>
          <p className="text-white/80 text-sm mb-6 max-w-lg mx-auto leading-relaxed">
            Nuestro equipo puede ayudarte a preparar entrevistas específicas con empresas europeas de forma personalizada.
          </p>
          <button
            onClick={() => navigate('/#contacto')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#1A2B4A] text-sm font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line"></i>
            Contactar con el equipo
          </button>
        </div>

        <div className="mt-8 text-center text-xs text-gray-400 pb-8 print:mt-4">
          <p>Guía elaborada por el equipo de BN Agenzia · Uso exclusivo para candidatos registrados</p>
        </div>
      </div>

      <style>{`
        @media print {
          @page { margin: 1.5cm; }
          body { font-size: 11pt; }
        }
      `}</style>
    </div>
  );
}
